<template>
  <el-card>
    <el-form inline>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable>
          <el-option label="待审核" value="pending" />
          <el-option label="审核通过" value="approved" />
          <el-option label="驳回" value="rejected" />
        </el-select>
      </el-form-item>
      <el-form-item label="关键词">
        <el-input v-model="query.keyword" placeholder="物种名/地址/备注" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="loadData">查询</el-button>
        <el-button type="warning" @click="batchRecheckAI">批量重新AI审核</el-button>
      </el-form-item>
    </el-form>
    <el-table :data="rows" border>
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="speciesName" label="物种" min-width="120" />
      <el-table-column prop="submittedBy" label="提交人" width="120" />
      <el-table-column label="图片" width="110">
        <template slot-scope="scope">
          <el-image
            v-if="scope.row.photoUrl"
            :src="scope.row.photoUrl"
            :preview-src-list="[scope.row.photoUrl]"
            fit="cover"
            class="record-image"
          />
          <span v-else>无图片</span>
        </template>
      </el-table-column>
      <el-table-column label="数量" min-width="150">
        <template slot-scope="scope">
          {{ formatQuantity(scope.row) }}
        </template>
      </el-table-column>
      <el-table-column label="AI判断" width="200">
        <template slot-scope="scope">
          <div v-if="scope.row.aiResult === 'approved'">
            <el-tag type="success" size="small">有效</el-tag>
            <div v-if="scope.row.aiSpeciesGuess" style="color: #67c23a; font-size: 11px; margin-top: 3px;">识别：{{ scope.row.aiSpeciesGuess }}</div>
          </div>
          <div v-else-if="scope.row.aiResult === 'rejected'">
            <el-tag type="danger" size="small">无效</el-tag>
            <div v-if="scope.row.aiRejectReason" style="color: #f56c6c; font-size: 11px; margin-top: 3px;">原因：{{ aiRejectReasonLabel(scope.row.aiRejectReason) }}</div>
            <div v-if="scope.row.aiReviewText" style="color: #909399; font-size: 11px; margin-top: 2px; white-space: normal; line-height: 1.4;">{{ scope.row.aiReviewText }}</div>
          </div>
          <span v-else style="color: #909399; font-size: 12px;">未判断</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="120">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.status === 'pending'" type="warning">待审核</el-tag>
          <el-tag v-else-if="scope.row.status === 'approved'" type="success">审核通过</el-tag>
          <el-tag v-else-if="scope.row.status === 'rejected'" type="danger">已驳回</el-tag>
          <span v-else>{{ scope.row.status }}</span>
        </template>
      </el-table-column>
      <el-table-column label="位置" min-width="250" show-overflow-tooltip>
        <template slot-scope="scope">
          <div v-if="scope.row.latitude && scope.row.longitude">
            <div v-if="scope.row.address" style="margin-bottom: 4px;">{{ scope.row.address }}</div>
            <div style="color: #909399; font-size: 12px;">
              经度 {{ scope.row.longitude }} · 纬度 {{ scope.row.latitude }}
            </div>
          </div>
          <span v-else>无位置信息</span>
        </template>
      </el-table-column>
      <el-table-column prop="note" label="备注" min-width="150" show-overflow-tooltip />
      <el-table-column label="提交时间" width="180">
        <template slot-scope="scope">
          {{ formatTime(scope.row.submittedAt) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="220" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" type="success" @click="approve(scope.row)" v-if="scope.row.status==='pending'">通过</el-button>
          <el-button size="mini" type="danger" @click="reject(scope.row)" v-if="scope.row.status==='pending'">驳回</el-button>
          <el-button size="mini" type="danger" @click="deleteRow(scope.row)" v-if="scope.row.status==='approved' || scope.row.status==='rejected'">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      @current-change="handlePageChange"
      :current-page="query.page"
      :page-size="query.pageSize"
      layout="total, prev, pager, next"
      :total="total"
      style="margin-top: 20px; text-align: right;"
    />
  </el-card>
</template>

<script>
import { getRecordPage, approveRecord, rejectRecord, deleteRecord, batchRecheckAI } from '@/api'

export default {
  data () {
    return {
      query: { page: 1, pageSize: 10, status: '', keyword: '' },
      rows: [],
      total: 0,
      projectMap: {
        'apple-snail': '福寿螺',
        'red-fire-ant': '红火蚁',
        'fall-armyworm': '草地贪夜蛾',
        'goldenrod': '加拿大一枝黄花'
      }
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    async loadData () {
      const res = await getRecordPage(this.query)
      const pageData = res && res.data ? res.data : res
      this.rows = (pageData && pageData.rows) || []
      this.total = (pageData && pageData.total) || 0
    },
    handlePageChange (page) {
      this.query.page = page
      this.loadData()
    },
    async approve (row) {
      await approveRecord(row.id)
      this.$message.success('已通过')
      this.loadData()
    },
    async reject (row) {
      await rejectRecord(row.id, { rejectReason: 'other' })
      this.$message.success('已驳回')
      this.loadData()
    },
    async deleteRow (row) {
      try {
        await this.$confirm('确定要删除这条记录吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await deleteRecord(row.id)
        this.$message.success('删除成功')
        this.loadData()
      } catch (err) {
        if (err !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    async batchRecheckAI () {
      try {
        await this.$confirm('确定要对当前筛选条件下的所有记录重新进行AI审核吗？', '批量重新AI审核', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await batchRecheckAI(this.query.status)
        this.$message.success('已提交批量AI审核任务，请稍后刷新查看结果')
        this.loadData()
      } catch (err) {
        if (err !== 'cancel') {
          this.$message.error('批量AI审核失败')
        }
      }
    },
    getProjectName (projectId) {
      return this.projectMap[projectId] || projectId
    },
    formatQuantity (row) {
      if (row.projectId === 'apple-snail') {
        const eggPart = row.eggCount ? `卵块数${row.eggCount}个` : ''
        const individualPart = row.individualCount ? `个体数${row.individualCount}个` : ''
        if (eggPart && individualPart) {
          return `${eggPart}  ${individualPart}`
        }
        return eggPart || individualPart || '-'
      } else if (row.projectId === 'goldenrod') {
        return row.abundance ? `${row.abundance}枝` : '-'
      } else {
        if (row.abundance && row.abundanceUnit) {
          return `${row.abundance}${row.abundanceUnit}`
        } else if (row.abundance) {
          return `${row.abundance}`
        }
        return '-'
      }
    },
    aiRejectReasonLabel (reason) {
      const map = {
        secondary_photo: '拍摄屏幕/印刷品',
        stock_photo: '疑似图库照片',
        not_target_species: '非目标物种',
        insufficient_evidence: '特征不足以确认',
        poor_image_quality: '图像质量差'
      }
      return map[reason] || reason
    },
    formatTime (timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }
  }
}
</script>

<style scoped>
.record-image {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  display: block;
}
</style>
